import { FC, FormEvent, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useResetPassword, ApiError } from '@centrix/api-client';
import {
  Alert,
  Button,
  CollapsibleContainer,
  Heading,
  Paragraph,
  PasswordInputField,
  Spinner,
} from '@centrix/shared-ui';

import { AuthLayout } from '@/components/AuthLayout';
import { type ResetPasswordValidationErrors, validateResetPassword } from '@/utils/authValidation';

const INVALID_LINK_MESSAGE =
  'This password reset link is invalid or has expired. Please request a new one.';

const backToLoginLink = (
  <Link
    to="/login"
    className="mt-3 block text-center text-sm leading-5 font-medium text-brand-primary hover:underline"
  >
    Back to Login
  </Link>
);

const forgotPasswordLink = (
  <Link
    to="/forgot-password"
    className="mt-3 block text-center text-sm leading-5 font-medium text-brand-primary hover:underline"
  >
    Request a new link
  </Link>
);

export const ResetPasswordPage: FC = () => {
  const { uid, token: pathToken } = useParams<{ uid: string; token: string }>();
  return <ResetPasswordForm uid={uid} token={pathToken} />;
};

interface ResetPasswordFormProps {
  uid: string | undefined;
  token: string | undefined;
}

const ResetPasswordForm: FC<ResetPasswordFormProps> = ({ uid, token }) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState<ResetPasswordValidationErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [isComplete, setIsComplete] = useState(false);

  const resetPassword = useResetPassword();

  const hasValidLink = Boolean(uid && token);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setFormError(null);

    const errors = validateResetPassword({ newPassword, confirmPassword });
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});

    if (!uid || !token) {
      setFormError(INVALID_LINK_MESSAGE);
      return;
    }

    try {
      await resetPassword.mutateAsync({
        uid,
        token,
        new_password: newPassword,
        confirm_new_password: confirmPassword,
      });
      setIsComplete(true);
    } catch (err) {
      if (err instanceof ApiError && err.status === 400) {
        const data = err.data as
          | {
              uid?: string;
              token?: string;
              new_password?: string[];
              confirm_new_password?: string[];
            }
          | undefined;
        if (data?.uid || data?.token) {
          setFormError(INVALID_LINK_MESSAGE);
        } else if (data?.new_password?.length) {
          setFieldErrors((prev) => ({ ...prev, newPassword: data.new_password?.[0] }));
        } else if (data?.confirm_new_password?.length) {
          setFieldErrors((prev) => ({ ...prev, confirmPassword: data.confirm_new_password?.[0] }));
        } else {
          setFormError(err.message);
        }
      } else if (err instanceof ApiError) {
        setFormError(err.message);
      } else {
        setFormError('Unable to update your password right now. Please try again.');
      }
    }
  };

  const isSubmitting = resetPassword.isPending;

  if (!hasValidLink) {
    return (
      <AuthLayout>
        <Heading as="h2" className="text-[22px] leading-7 font-medium text-black mb-2">
          Invalid reset link
        </Heading>
        <Paragraph className="text-sm leading-6 text-gray-body mb-6">
          {INVALID_LINK_MESSAGE}
        </Paragraph>
        {forgotPasswordLink}
      </AuthLayout>
    );
  }

  if (isComplete) {
    return (
      <AuthLayout>
        <Heading as="h2" className="text-[22px] leading-7 font-medium text-black mb-2">
          Password updated
        </Heading>
        <Paragraph className="text-sm leading-6 text-gray-body mb-6">
          Your password has been updated. You can now log in with your new password.
        </Paragraph>
        {backToLoginLink}
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <Heading as="h2" className="text-[22px] leading-7 font-medium text-black mb-6">
        Create a new password
      </Heading>

      <CollapsibleContainer isOpen={!!formError} className="mb-4">
        {formError && (
          <Alert variant="destructive" onDismiss={() => setFormError(null)}>
            {formError}
          </Alert>
        )}
      </CollapsibleContainer>

      <form onSubmit={handleSubmit} className="space-y-2.5">
        <PasswordInputField
          id="new-password"
          label="New Password"
          name="new-password"
          value={newPassword}
          onChange={(e) => {
            setNewPassword(e.target.value);
            setFieldErrors((prev) => ({ ...prev, newPassword: undefined }));
          }}
          placeholder="Create a strong password"
          disabled={isSubmitting}
          autoComplete="new-password"
          error={fieldErrors.newPassword}
          inputClassName="h-11 px-4 pr-12 text-sm text-black placeholder:text-gray-placeholder focus-visible:ring-offset-0 disabled:opacity-100 disabled:bg-gray-100 border-gray-placeholder"
        />

        <PasswordInputField
          id="confirm-password"
          label="Retype Password"
          name="confirm-password"
          value={confirmPassword}
          onChange={(e) => {
            setConfirmPassword(e.target.value);
            setFieldErrors((prev) => ({ ...prev, confirmPassword: undefined }));
          }}
          placeholder="Re-enter your password"
          disabled={isSubmitting}
          autoComplete="new-password"
          error={fieldErrors.confirmPassword}
          inputClassName="h-11 px-4 pr-12 text-sm text-black placeholder:text-gray-placeholder focus-visible:ring-offset-0 disabled:opacity-100 disabled:bg-gray-100 border-gray-placeholder"
        />

        <Button
          type="submit"
          variant="auth"
          size="auth"
          disabled={isSubmitting}
          className="mt-3.5 w-full leading-6"
        >
          {isSubmitting ? (
            <>
              <Spinner className="text-white" />
              <span>Updating...</span>
            </>
          ) : (
            'Update Password'
          )}
        </Button>
      </form>
    </AuthLayout>
  );
};
